import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Gauge, CheckCircle2, Flag, RotateCcw, Filter, Loader2, X, AlertTriangle, BadgeCheck,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AuthedImage, { PhotoLightbox } from "../components/AuthedImage";
import EmptyState from "../components/EmptyState";
import dict from "../i18n/es-MX";

const STATE_FILTERS = [
    { key: "pending",  qs: { verified: false, flagged: false } },
    { key: "verified", qs: { verified: true } },
    { key: "flagged",  qs: { flagged: true } },
    { key: "all",      qs: {} },
];

// Map external (?status=) values → internal tab key.
const STATUS_PARAM_TO_TAB = {
    pendientes: "pending",
    verificadas: "verified",
    marcadas: "flagged",
    todas: "all",
};

import { fmtDateTime as fmtDT } from "../lib/dateFormat";

export default function Verification() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialTab = STATUS_PARAM_TO_TAB[searchParams.get("status")] || "pending";
    const [stats, setStats] = useState({ pending: 0, verified: 0, verified_this_month: 0, flagged: 0 });
    const [tab, setTabState] = useState(initialTab);
    const setTab = (next) => {
        setTabState(next);
        // Keep URL in sync so deep links remain shareable.
        const reverse = Object.entries(STATUS_PARAM_TO_TAB).find(([, v]) => v === next);
        if (reverse) {
            const sp = new URLSearchParams(searchParams);
            sp.set("status", reverse[0]);
            setSearchParams(sp, { replace: true });
        }
    };
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null); // reading id
    const [err, setErr] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const [flagOpen, setFlagOpen] = useState(null); // reading id
    const [flagReason, setFlagReason] = useState("");
    const [assets, setAssets] = useState({});
    const [jobs, setJobs] = useState({});
    const [operators, setOperators] = useState({});

    const isAdmin = user?.role === "admin";

    const loadStats = useCallback(async () => {
        try {
            const r = await api.get("/meter-readings/stats");
            setStats(r.data);
        } catch (e) { /* silent */ }
    }, []);

    const load = useCallback(async () => {
        setLoading(true); setErr("");
        const def = STATE_FILTERS.find(f => f.key === tab) || STATE_FILTERS[0];
        try {
            const [a, j, o, r] = await Promise.all([
                api.get("/assets"), api.get("/jobs"), api.get("/operators"),
                api.get("/meter-readings", { params: def.qs }),
            ]);
            setAssets(Object.fromEntries(a.data.map(x => [x.id, x])));
            setJobs(Object.fromEntries(j.data.map(x => [x.id, x])));
            setOperators(Object.fromEntries(o.data.map(x => [x.id, x])));
            setRows(r.data || []);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }, [tab]);

    useEffect(() => { loadStats(); load(); }, [load, loadStats]);

    const doVerify = async (rid) => {
        setBusy(rid); setErr("");
        try {
            await api.patch(`/meter-readings/${rid}/verify`, {});
            await Promise.all([load(), loadStats()]);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const doFlag = async () => {
        if (!flagOpen || flagReason.trim().length < 2) return;
        setBusy(flagOpen); setErr("");
        try {
            await api.patch(`/meter-readings/${flagOpen}/flag`, { flag_reason: flagReason.trim() });
            setFlagOpen(null); setFlagReason("");
            await Promise.all([load(), loadStats()]);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const doReset = async (rid) => {
        if (!isAdmin) return;
        setBusy(rid); setErr("");
        try {
            await api.patch(`/meter-readings/${rid}/reset`, {});
            await Promise.all([load(), loadStats()]);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(null); }
    };

    const kpis = [
        { label: dict.verify.pending, value: stats.pending, key: "pending", color: "amber" },
        { label: dict.verify.verifiedMonth, value: stats.verified_this_month, key: "verified", color: "emerald" },
        { label: dict.verify.flagged, value: stats.flagged, key: "flagged", color: "red" },
    ];

    return (
        <div className="space-y-5" data-testid="verification-page">
            <header className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight inline-flex items-center gap-2">
                        <Gauge className="text-brand-orange" size={26}/> {dict.verify.title}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">{dict.verify.subtitle}</p>
                </div>
            </header>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {kpis.map(k => (
                    <button key={k.key} type="button" onClick={() => setTab(k.key)} data-testid={`kpi-${k.key}`}
                        className={`text-left rounded-2xl border p-4 transition ${tab === k.key ? `border-brand-orange bg-brand-orange/5` : "border-slate-800 bg-slate-900/60 hover:border-slate-600"}`}>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">{k.label}</div>
                        <div className={`font-display text-3xl font-extrabold mt-1 text-${k.color}-300`}>{k.value}</div>
                    </button>
                ))}
            </div>

            <div className="flex items-center gap-2 border-b border-slate-800">
                {STATE_FILTERS.map(f => (
                    <button key={f.key} type="button" onClick={() => setTab(f.key)} data-testid={`filter-${f.key}`}
                        className={`-mb-px px-4 h-9 text-xs font-semibold uppercase tracking-wider border-b-2 transition ${tab === f.key ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-400 hover:text-slate-100"}`}>
                        {dict.verify.filter[f.key]}
                    </button>
                ))}
            </div>

            {err && <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200" data-testid="verify-err">{err}</div>}
            {loading ? <div className="text-center py-12"><Loader2 className="animate-spin inline text-slate-500"/></div> :
              rows.length === 0 ? <EmptyState icon={BadgeCheck} title={dict.verify.empty} testid="verify-empty"/> :
              <div className="space-y-3">
                {rows.map(r => {
                    const asset = assets[r.asset_id] || {};
                    const job = jobs[r.job_id] || {};
                    const op = operators[r.operator_id] || {};
                    const unit = r.kind === "odometer_km" ? "km" : "h";
                    return (
                        <div key={r.id} data-testid={`reading-card-${r.id}`}
                            className={`rounded-xl border ${r.flagged ? "border-red-500/40 bg-red-500/5" : r.verified ? "border-emerald-500/40 bg-emerald-500/5" : "border-slate-800 bg-slate-900/40"} p-4 flex gap-4 flex-wrap sm:flex-nowrap`}>
                            <button type="button" onClick={() => r.photo_id && setLightbox({ id: r.photo_id, caption: `${asset.nickname} • ${r.value} ${unit}` })}
                                className="w-24 h-24 shrink-0 rounded-lg overflow-hidden border border-slate-800 hover:border-brand-orange disabled:opacity-40"
                                disabled={!r.photo_id} data-testid={`reading-thumb-${r.id}`}>
                                <AuthedImage photoId={r.photo_id} className="w-full h-full object-cover" placeholderClass="w-full h-full"/>
                            </button>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-baseline gap-2">
                                    <div className="font-display text-3xl font-bold text-slate-100">{r.value}</div>
                                    <span className="text-xs uppercase tracking-wider text-slate-400">{unit === "km" ? dict.verify.kmLabel : dict.verify.hoursLabel}</span>
                                    {r.verified && <span className="px-2 h-5 text-[10px] rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1 uppercase tracking-wider"><CheckCircle2 size={10}/>{dict.verify.verifiedBadge}</span>}
                                    {r.flagged && <span className="px-2 h-5 text-[10px] rounded-full bg-orange-500/15 text-orange-300 border border-orange-500/40 inline-flex items-center gap-1 uppercase tracking-wider"><AlertTriangle size={10}/>{dict.verify.flaggedBadge}</span>}
                                </div>
                                <div className="text-xs text-slate-400 mt-1 space-y-0.5">
                                    <div><span className="text-slate-500">{dict.verify.asset}:</span> <span className="text-slate-200">{asset.nickname || "—"}</span></div>
                                    <div><span className="text-slate-500">{dict.verify.job}:</span> <span className="text-slate-200">{job.code || "—"}</span></div>
                                    <div><span className="text-slate-500">{dict.verify.operator}:</span> <span className="text-slate-200">{op.full_name || "—"}</span></div>
                                    <div><span className="text-slate-500">{dict.verify.takenAt}:</span> <span className="text-slate-200">{fmtDT(r.taken_at)}</span></div>
                                </div>
                                {r.flagged && r.flag_reason && (
                                    <div className="mt-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-xs px-2 py-1.5" data-testid={`flag-reason-${r.id}`}>
                                        ⚑ {r.flag_reason}
                                    </div>
                                )}
                            </div>
                            <div className="flex sm:flex-col gap-2 sm:w-44 shrink-0">
                                {!r.verified && (
                                    <button type="button" onClick={() => doVerify(r.id)} disabled={busy === r.id}
                                        data-testid={`verify-btn-${r.id}`}
                                        className="flex-1 h-10 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                                        {busy === r.id ? <Loader2 className="animate-spin" size={12}/> : <CheckCircle2 size={13}/>} {dict.verify.confirm}
                                    </button>
                                )}
                                {!r.flagged && (
                                    <button type="button" onClick={() => { setFlagOpen(r.id); setFlagReason(""); }} disabled={busy === r.id}
                                        data-testid={`flag-btn-${r.id}`}
                                        className="flex-1 h-10 rounded-lg border border-amber-500/60 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                                        <Flag size={13}/> {dict.verify.flag}
                                    </button>
                                )}
                                {isAdmin && (r.verified || r.flagged) && (
                                    <button type="button" onClick={() => doReset(r.id)} disabled={busy === r.id}
                                        data-testid={`reset-btn-${r.id}`}
                                        className="flex-1 h-10 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-400 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5">
                                        <RotateCcw size={13}/> {dict.verify.reset}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
              </div>
            }

            <PhotoLightbox photoId={lightbox?.id} caption={lightbox?.caption} onClose={() => setLightbox(null)}/>

            {flagOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setFlagOpen(null)} data-testid="flag-modal">
                    <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h2 className="font-display text-lg font-bold inline-flex items-center gap-2"><Flag size={16} className="text-amber-400"/> {dict.verify.flagTitle}</h2>
                            <button type="button" onClick={() => setFlagOpen(null)} className="w-8 h-8 rounded hover:bg-slate-800 inline-flex items-center justify-center"><X size={16}/></button>
                        </div>
                        <p className="text-sm text-slate-400">{dict.verify.flagHelp}</p>
                        <textarea value={flagReason} onChange={e => setFlagReason(e.target.value)} maxLength={400}
                            data-testid="flag-reason-input"
                            placeholder={dict.verify.flagPlaceholder}
                            className="w-full min-h-[100px] px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-100"/>
                        <div className="flex gap-2">
                            <button type="button" onClick={() => setFlagOpen(null)} className="flex-1 h-10 rounded-lg border border-slate-700 hover:bg-slate-800 text-sm">{dict.common.cancel}</button>
                            <button type="button" onClick={doFlag} disabled={flagReason.trim().length < 2 || busy === flagOpen} data-testid="flag-submit"
                                className="flex-1 h-10 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 text-sm font-bold uppercase tracking-wider inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                                {busy === flagOpen ? <Loader2 className="animate-spin" size={13}/> : <Flag size={13}/>} {dict.verify.flagConfirm}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
